/**
 * 菜单国际化翻译工具
 * 将后端返回的菜单标题转换为对应的国际化键
 */

import type { useIntl } from '@umijs/max';

// 菜单标题到国际化键的映射表
const MENU_TITLE_TO_INTL_KEY: Record<string, string> = {
  // 系统管理模块
  系统管理: 'menu.system',
  字典管理: 'menu.system.dict',
  菜单管理: 'menu.system.menu',
  角色管理: 'menu.system.role',
  系统配置: 'menu.system.sysConfig',
  无认证接口: 'menu.system.noAuth',

  // 事件管理模块
  事件管理: 'menu.event',
  事件记录: 'menu.event.eventRecord',
  事件工单: 'menu.event.eventOrder',
  工单节点: 'menu.event.eventOrderNode',
  事件接收者: 'menu.event.eventReceiver',
  资源文件: 'menu.event.resourceFile',
  事件类型: 'menu.event.eventType',

  // 组织管理模块
  组织管理: 'menu.organization',
  组织架构: 'menu.organization.orgTree',
  部门管理: 'menu.organization.orgDept',
  岗位管理: 'menu.organization.orgPost',
  下级组织: 'menu.organization.orgTree',
  员工管理: 'menu.organization.employee',
  组织配置: 'menu.organization.orgConfig',

  // 设备管理模块
  设备管理: 'menu.device',
  设备型号: 'menu.device.deviceModel',
  设备供应商: 'menu.device.deviceSupplier',
  设备网关: 'menu.device.deviceGateway',
  设备终端: 'menu.device.deviceTerminal',

  // 套餐管理模块
  套餐管理: 'menu.package',
  我的套餐: 'menu.package.myPackage',
  子级套餐: 'menu.package.subPackage',
  下级套餐: 'menu.package.subPackage',

  // 用户管理
  用户管理: 'menu.user',
  用户列表: 'menu.user.list',

  // 欢迎页面
  欢迎页面: 'menu.welcome',

  // 视频融合模块
  视频融合: 'menu.video',
  实时预览: 'menu.video.preview',
  历史回放: 'menu.video.playback',
  手动录像: 'menu.video.recording',
  视频预览: 'menu.video.preview',
  视频文件管理: 'menu.video.fileManagement',
  录制记录管理: 'menu.video.recordingRecord',

  // 英文对应关系（如果后端返回英文标题）
  'System Management': 'menu.system',
  'Dictionary Management': 'menu.system.dict',
  'Menu Management': 'menu.system.menu',
  'Role Management': 'menu.system.role',
  'System Configuration': 'menu.system.sysConfig',
  'No Auth Interface': 'menu.system.noAuth',

  'Event Management': 'menu.event',
  'Event Record': 'menu.event.eventRecord',
  'Event Order': 'menu.event.eventOrder',
  'Order Node': 'menu.event.eventOrderNode',
  'Event Receiver': 'menu.event.eventReceiver',
  'Resource File': 'menu.event.resourceFile',
  'Event Type': 'menu.event.eventType',

  'Organization Management': 'menu.organization',
  'Department Management': 'menu.organization.orgDept',
  'Position Management': 'menu.organization.orgPost',
  'Subordinate Organizations': 'menu.organization.orgTree',
  'Employee Management': 'menu.organization.employee',
  'Organization Config': 'menu.organization.orgConfig',

  'Device Management': 'menu.device',
  'Device Model': 'menu.device.deviceModel',
  'Device Supplier': 'menu.device.deviceSupplier',
  'Device Gateway': 'menu.device.deviceGateway',
  'Device Terminal': 'menu.device.deviceTerminal',

  'Package Management': 'menu.package',
  'My Package': 'menu.package.myPackage',
  'Sub Package': 'menu.package.subPackage',
  'Child Package': 'menu.package.subPackage',

  'User Management': 'menu.user',
  'Welcome Page': 'menu.welcome',

  // Video Fusion Module (English)
  'Video Fusion': 'menu.video',
  'Real-time Preview': 'menu.video.preview',
  'History Playback': 'menu.video.playback',
  'Manual Recording': 'menu.video.recording',
  'Video File Management': 'menu.video.fileManagement',
  'Recording Record Management': 'menu.video.recordingRecord',
};

/**
 * 将菜单标题转换为国际化键
 * @param menuTitle 菜单标题
 * @returns 对应的国际化键，如果没有匹配则返回原标题
 */
export function getMenuIntlKey(menuTitle: string): string {
  // 清理标题（去除多余空格）
  const cleanTitle = menuTitle.trim();

  // 直接匹配
  if (MENU_TITLE_TO_INTL_KEY[cleanTitle]) {
    return MENU_TITLE_TO_INTL_KEY[cleanTitle];
  }

  // 尝试部分匹配（处理可能的标点符号差异）
  const matchedKey = Object.keys(MENU_TITLE_TO_INTL_KEY).find(
    (key) => cleanTitle.includes(key) || key.includes(cleanTitle),
  );

  if (matchedKey) {
    return MENU_TITLE_TO_INTL_KEY[matchedKey];
  }

  // 如果没有找到匹配，返回原标题作为fallback
  console.warn(`⚠️ 未找到菜单标题 "${cleanTitle}" 的国际化键映射`);
  return cleanTitle;
}

/**
 * 获取翻译后的菜单标题
 * @param menuTitle 原始菜单标题
 * @param intl 国际化实例
 * @returns 翻译后的标题
 */
export function getTranslatedMenuTitle(
  menuTitle: string,
  intl: ReturnType<typeof useIntl>,
): string {
  const intlKey = getMenuIntlKey(menuTitle);

  // 如果返回的是国际化键，则进行翻译
  if (intlKey.startsWith('menu.')) {
    const translated = intl.formatMessage({ id: intlKey });
    if (translated && translated !== intlKey) {
      return translated;
    }
  }

  // 如果翻译失败或返回的就是原文，则返回原始标题
  return menuTitle;
}

/**
 * 批量翻译菜单数据
 * @param menus 菜单数据数组
 * @param intl 国际化实例
 * @returns 翻译后的菜单数据
 */
export function translateMenus(
  menus: any[],
  intl: ReturnType<typeof useIntl>,
): any[] {
  if (!menus || menus.length === 0) {
    return [];
  }

  return menus.map((menu) => {
    const translatedMenu = {
      ...menu,
      name: getTranslatedMenuTitle(menu.menuTitle || menu.name, intl),
    };

    // 递归处理子菜单
    if (menu.children && menu.children.length > 0) {
      translatedMenu.children = translateMenus(menu.children, intl);
    }

    return translatedMenu;
  });
}

/**
 * 增强的菜单转换函数，包含国际化支持
 * @param menus 原始菜单数据
 * @param options 转换选项
 * @returns 转换后的菜单数据
 */
export function transformMenusWithTranslation(
  menus: API.AccessMenusItem[],
  options: {
    includeHidden?: boolean;
    forNavigation?: boolean;
    intl?: ReturnType<typeof useIntl>;
  } = {},
) {
  const { includeHidden = false, forNavigation = true, intl } = options;

  if (!menus || menus.length === 0) {
    return [];
  }

  // 过滤菜单
  const filteredMenus = menus.filter((menu) => menu.enabled === '1');

  const navigationFilteredMenus = forNavigation
    ? includeHidden
      ? filteredMenus
      : filteredMenus.filter((menu) => menu.isShow !== '0')
    : filteredMenus;

  return navigationFilteredMenus
    .sort((a, b) => (a.weight || 0) - (b.weight || 0))
    .map((menu) => {
      // 获取翻译后的标题
      let translatedName = menu.menuTitle || '';
      if (intl && menu.menuTitle) {
        translatedName = getTranslatedMenuTitle(menu.menuTitle, intl);
      }

      const menuItem: any = {
        name: translatedName,
        path: menu.menuPath,
        icon: menu.icon, // 图标将在后续处理中转换
        __rawMenu: menu,
      };

      // 处理子菜单
      if (menu.children && menu.children.length > 0) {
        menuItem.children = transformMenusWithTranslation(menu.children, {
          ...options,
          intl,
        });
      }

      return menuItem;
    });
}
